import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import { db } from "@/firebase";

/* =========================================================
   References
   ========================================================= */

function requestCollectionRef(venueId) {
  return collection(
    db,
    "venues",
    venueId,
    "artwork_requests"
  );
}

function requestDocRef(
  venueId,
  requestId
) {
  return doc(
    db,
    "venues",
    venueId,
    "artwork_requests",
    requestId
  );
}

/* =========================================================
   Normalisation
   ========================================================= */

function normaliseArtworkList(data = {}) {
  const savedArtworks =
    Array.isArray(data.artworks)
      ? data.artworks.filter(
          (item) =>
            item &&
            item.downloadUrl
        )
      : [];

  if (savedArtworks.length) {
    return savedArtworks;
  }

  /*
   * Legacy support:
   * older requests only have artwork rather than artworks[].
   */
  if (
    data.artwork &&
    data.artwork.downloadUrl
  ) {
    return [
      data.artwork,
    ];
  }

  return [];
}

/* =========================================================
   Venue
   ========================================================= */

export async function fetchVenue(
  venueId
) {
  if (!venueId) {
    return null;
  }

  const venueSnap =
    await getDoc(
      doc(
        db,
        "venues",
        venueId
      )
    );

  return venueSnap.exists()
    ? venueSnap.data() || {}
    : null;
}

/* =========================================================
   Artwork requests
   ========================================================= */

export async function fetchArtworkRequests(
  venueId
) {
  if (!venueId) {
    return [];
  }

  const requestsQuery =
    query(
      requestCollectionRef(
        venueId
      ),
      orderBy(
        "createdAt",
        "desc"
      )
    );

  const snap =
    await getDocs(
      requestsQuery
    );

  return snap.docs.map(
    (requestSnap) => {
      const data =
        requestSnap.data() || {};

      return {
        id:
          requestSnap.id,

        ...data,

        artworks:
          normaliseArtworkList(
            data
          ),
      };
    }
  );
}

/* =========================================================
   Create request
   ========================================================= */

export async function createArtworkRequest({
  venueId,
  venueName,
  title,
  notes,
  dueDate,
  user,
  workflow,
}) {
  if (!venueId) {
    throw new Error(
      "Venue is required."
    );
  }

  if (!title) {
    throw new Error(
      "Request title is required."
    );
  }

  return addDoc(
    requestCollectionRef(
      venueId
    ),
    {
      title,
      notes,
      dueDate,

      venueId,
      venueName,

      requestedByUid:
        user?.uid || "",

      requestedByEmail:
        user?.email || "",

      requestedByName:
        user?.displayName || "",

      workflow,

      /*
       * artwork remains the latest artwork for
       * backwards compatibility / preview support.
       *
       * artworks contains every uploaded file.
       */
      artwork: null,
      artworks: [],

      /*
       * Active / completed state.
       */
      closed: false,
      status: "active",

      completedAt: null,
      completedByUid: "",
      completedByEmail: "",

      createdAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    }
  );
}

/* =========================================================
   Save artwork
   ========================================================= */

export async function saveArtworkToRequest({
  venueId,
  requestId,
  artwork,
  workflow,
}) {
  if (
    !venueId ||
    !requestId ||
    !artwork
  ) {
    throw new Error(
      "Venue, request and artwork are required."
    );
  }

  await updateDoc(
    requestDocRef(
      venueId,
      requestId
    ),
    {
      /*
       * Latest artwork.
       */
      artwork,

      /*
       * Preserve every uploaded artwork file.
       */
      artworks:
        arrayUnion(
          artwork
        ),

      workflow,

      updatedAt:
        serverTimestamp(),
    }
  );
}

/* =========================================================
   Update workflow
   ========================================================= */

export async function updateArtworkWorkflow({
  venueId,
  requestId,
  workflow,
}) {
  if (
    !venueId ||
    !requestId
  ) {
    throw new Error(
      "Venue and artwork request are required."
    );
  }

  await updateDoc(
    requestDocRef(
      venueId,
      requestId
    ),
    {
      workflow,

      updatedAt:
        serverTimestamp(),
    }
  );
}

/* =========================================================
   Close / complete artwork request
   ========================================================= */

export async function closeArtworkRequest({
  venueId,
  requestId,
  user,
}) {
  if (
    !venueId ||
    !requestId
  ) {
    throw new Error(
      "Venue and artwork request are required."
    );
  }

  const requestReference =
    requestDocRef(
      venueId,
      requestId
    );

  await updateDoc(
    requestReference,
    {
      closed: true,

      status:
        "completed",

      completedAt:
        serverTimestamp(),

      completedByUid:
        user?.uid || "",

      completedByEmail:
        user?.email || "",

      updatedAt:
        serverTimestamp(),
    }
  );

  /*
   * Read the request back so the UI gets the actual
   * Firestore server timestamp rather than estimating it
   * in the browser.
   */
  const updatedSnapshot =
    await getDoc(
      requestReference
    );

  const updatedData =
    updatedSnapshot.exists()
      ? updatedSnapshot.data() || {}
      : {};

  return {
    closed: true,

    status:
      "completed",

    completedAt:
      updatedData.completedAt ||
      new Date().toISOString(),

    completedByUid:
      updatedData.completedByUid ||
      user?.uid ||
      "",

    completedByEmail:
      updatedData.completedByEmail ||
      user?.email ||
      "",
  };
}

/* =========================================================
   Send proof
   ========================================================= */

export async function sendArtworkProof({
  venueId,
  requestId,
}) {
  const functions =
    getFunctions();

  const sendArtworkProofEmail =
    httpsCallable(
      functions,
      "sendArtworkProofEmail"
    );

  const response =
    await sendArtworkProofEmail({
      venueId,
      requestId,
    });

  return (
    response?.data ||
    {}
  );
}

/* =========================================================
   Delete request
   ========================================================= */

export async function deleteArtworkRequest(
  venueId,
  requestId
) {
  if (
    !venueId ||
    !requestId
  ) {
    throw new Error(
      "Venue and artwork request are required."
    );
  }

  await deleteDoc(
    requestDocRef(
      venueId,
      requestId
    )
  );
}